import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'placeholder'
	| 'submitLabel'
	| 'subtypeAdjustedFlagTitle'
	| 'subtypeAdjustedFlagDescription'
	| 'remixVariantsLabel'
	| 'remixVariantsAriaLabel'
	| 'analyzing'
	| 'stopLabel'
	| 'remixVariantLineChart'
	| 'remixVariantAreaChart'
	| 'remixVariantBarChart'
	| 'remixVariantFunnelChart'
	| 'remixVariantPieChart'
	| 'remixVariantGanttChart'
	| 'remixVariantMindMap'
	| 'remixVariantFlowChart'
	| 'remixVariantModernStyle'
	| 'remixVariantBoldStyle'
	| 'remixVariantMinimalistStyle'
	| 'remixVariantSketchStyle'
	| 'remixVariantFlipCard'
	| 'remixVariantQuadrant'
	| 'remixVariantTreeMap'
	| 'remixVariantTimeline'
	| 'remixVariantGeographicalMap'
	| 'remixVariantOrgChart'
	| 'remixVariantImage'
	| 'chartType'
	| 'diagramType'
	| 'infographicType'
	| 'visualAidType'
	| 'changeChartTitle'
	| 'changeDiagramTitle'
	| 'changeInfographicTitle'
	| 'changeVisualAidTitle'
	| 'chatUpdateLineChart'
	| 'chatUpdateAreaChart'
	| 'chatUpdateBarChart'
	| 'chatUpdateFunnelChart'
	| 'chatUpdatePieChart'
	| 'chatUpdateGanttChart'
	| 'chatUpdateMindMap'
	| 'chatUpdateFlowChart'
	| 'chatUpdateModernStyle'
	| 'chatUpdateBoldStyle'
	| 'chatUpdateMinimalistStyle'
	| 'chatUpdateSketchStyle'
	| 'chatUpdateFlipCard'
	| 'chatUpdateQuadrant'
	| 'chatUpdateTreeMap'
	| 'chatUpdateTimeline'
	| 'chatUpdateGeographicalMap'
	| 'chatUpdateOrgChart'
	| 'chatUpdateImage';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		placeholder: {
			id: 'conversation-assistant.edit-with-rovo-bar.placeholder',
			defaultMessage: 'Ask Rovo anything...',
			description: 'Placeholder text shown in the edit with Rovo input bar for remix adjustments',
		},
		submitLabel: {
			id: 'conversation-assistant.edit-with-rovo-bar.submit-label',
			defaultMessage: 'Submit',
			description: 'Label for the submit button in the edit with Rovo bar',
		},
		subtypeAdjustedFlagTitle: {
			id: 'conversation-assistant.edit-with-rovo-bar.subtype-adjusted-flag-title',
			defaultMessage: 'Output adjusted',
			description:
				"Title of the informational flag shown after a remix completes when the user's selected output type was not compatible, so Rovo generated a different one instead",
		},
		subtypeAdjustedFlagDescription: {
			id: 'conversation-assistant.edit-with-rovo-bar.subtype-adjusted-flag-description',
			defaultMessage:
				"Your selection wasn't compatible with that output, so Rovo picked a new one.",
			description:
				"Body of the informational flag shown after a remix completes when the user's selected output type was not compatible, explaining that Rovo chose a different output type instead",
		},
		remixVariantsLabel: {
			id: 'conversation-assistant.edit-with-rovo-bar.remix-variants-label',
			defaultMessage: 'Remix variants',
			description: 'Label for the remix variants selector button',
		},
		remixVariantsAriaLabel: {
			id: 'conversation-assistant.edit-with-rovo-bar.remix-variants-aria-label',
			defaultMessage: '{remixVariantLabel} — {selectorLabel}',
			description: 'Accessible aria-label for the remix variants selector button',
		},
		analyzing: {
			id: 'conversation-assistant.edit-with-rovo-bar.analyzing',
			defaultMessage: 'Rovo is analyzing',
			description: 'Text displayed while Rovo is analyzing the remix adjustment input',
		},
		stopLabel: {
			id: 'conversation-assistant.edit-with-rovo-bar.stop-label',
			defaultMessage: 'Stop remixing',
			description:
				'Accessible label for the stop button in the edit-with-Rovo bar, used by screen readers to identify the action that halts the ongoing AI remix operation.',
		},
		remixVariantLineChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chart.line-chart',
			defaultMessage: 'Line graph',
			description: 'Remix option for displaying a line graph for the generated charts',
		},
		chatUpdateLineChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.line-chart',
			defaultMessage: 'Update to a line graph',
			description: 'Chat update message when selecting the line chart remix subtype',
		},
		remixVariantAreaChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chart.area-chart',
			defaultMessage: 'Area chart',
			description: 'Remix option for displaying an area chart for the generated charts',
		},
		chatUpdateAreaChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.area-chart',
			defaultMessage: 'Update to an area chart',
			description: 'Chat update message when selecting the area chart remix subtype',
		},
		remixVariantBarChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chart.bar-chart',
			defaultMessage: 'Bar chart',
			description: 'Remix option for displaying a bar chart for the generated charts',
		},
		chatUpdateBarChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.bar-chart',
			defaultMessage: 'Update to a bar chart',
			description: 'Chat update message when selecting the bar chart remix subtype',
		},
		remixVariantFunnelChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chart.funnel-chart',
			defaultMessage: 'Funnel chart',
			description: 'Remix option for displaying a funnel chart for the generated charts',
		},
		chatUpdateFunnelChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.funnel-chart',
			defaultMessage: 'Update to a funnel chart',
			description: 'Chat update message when selecting the funnel chart remix subtype',
		},
		remixVariantPieChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chart.pie-chart',
			defaultMessage: 'Pie chart',
			description: 'Remix option for displaying a pie chart for the generated charts',
		},
		chatUpdatePieChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.pie-chart',
			defaultMessage: 'Update to a pie chart',
			description: 'Chat update message when selecting the pie chart remix subtype',
		},
		remixVariantGanttChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chart.gantt-chart',
			defaultMessage: 'Gantt chart',
			description: 'Remix option for displaying a gantt chart for the generated charts',
		},
		chatUpdateGanttChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.gantt-chart',
			defaultMessage: 'Update to a Gantt chart',
			description: 'Chat update message when selecting the gantt chart remix subtype',
		},
		remixVariantMindMap: {
			id: 'conversation-assistant.edit-with-rovo-bar.diagram.mind-map',
			defaultMessage: 'Mindmap',
			description: 'Remix option for displaying a mind map for the generated diagrams',
		},
		chatUpdateMindMap: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.mind-map',
			defaultMessage: 'Update to a mindmap',
			description: 'Chat update message when selecting the mindmap remix subtype',
		},
		remixVariantFlowChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.diagram.flow-chart',
			defaultMessage: 'Flowchart',
			description: 'Remix option for displaying a flow chart for the generated diagrams',
		},
		chatUpdateFlowChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.flow-chart',
			defaultMessage: 'Update to a flowchart',
			description: 'Chat update message when selecting the flowchart remix subtype',
		},
		remixVariantModernStyle: {
			id: 'conversation-assistant.edit-with-rovo-bar.infographic.modern-style',
			defaultMessage: 'Modern style',
			description: 'Remix option for displaying a modern style for the generated infographics',
		},
		chatUpdateModernStyle: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.modern-style',
			defaultMessage: 'Update to a modern style',
			description: 'Chat update message when selecting the modern style remix subtype',
		},
		remixVariantBoldStyle: {
			id: 'conversation-assistant.edit-with-rovo-bar.infographic.bold-style',
			defaultMessage: 'Bold style',
			description: 'Remix option for displaying a bold style for the generated infographics',
		},
		chatUpdateBoldStyle: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.bold-style',
			defaultMessage: 'Update to a bold style',
			description: 'Chat update message when selecting the bold style remix subtype',
		},
		remixVariantMinimalistStyle: {
			id: 'conversation-assistant.edit-with-rovo-bar.infographic.minimalist-style',
			defaultMessage: 'Minimalist style',
			description: 'Remix option for displaying a minimalist style for the generated infographics',
		},
		chatUpdateMinimalistStyle: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.minimalist-style',
			defaultMessage: 'Update to a minimalist style',
			description: 'Chat update message when selecting the minimalist style remix subtype',
		},
		remixVariantSketchStyle: {
			id: 'conversation-assistant.edit-with-rovo-bar.infographic.sketch-style',
			defaultMessage: 'Sketch style',
			description: 'Remix option for displaying a sketch style for the generated infographics',
		},
		chatUpdateSketchStyle: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.sketch-style',
			defaultMessage: 'Update to a sketch style',
			description: 'Chat update message when selecting the sketch style remix subtype',
		},
		remixVariantFlipCard: {
			id: 'conversation-assistant.edit-with-rovo-bar.visual-aid.flip-card',
			defaultMessage: 'Flip card',
			description: 'Remix option for displaying a flip card for the generated visual aids',
		},
		chatUpdateFlipCard: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.flip-card',
			defaultMessage: 'Update to a flip card',
			description: 'Chat update message when selecting the flip card remix subtype',
		},
		remixVariantQuadrant: {
			id: 'conversation-assistant.edit-with-rovo-bar.visual-aid.quadrant',
			defaultMessage: 'Quadrant',
			description: 'Remix option for displaying a quadrant for the generated visual aids',
		},
		chatUpdateQuadrant: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.quadrant',
			defaultMessage: 'Update to a quadrant',
			description: 'Chat update message when selecting the quadrant remix subtype',
		},
		remixVariantTreeMap: {
			id: 'conversation-assistant.edit-with-rovo-bar.visual-aid.tree-map',
			defaultMessage: 'Tree map',
			description: 'Remix option for displaying a tree map for the generated visual aids',
		},
		chatUpdateTreeMap: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.tree-map',
			defaultMessage: 'Update to a tree map',
			description: 'Chat update message when selecting the tree map remix subtype',
		},
		remixVariantTimeline: {
			id: 'conversation-assistant.edit-with-rovo-bar.visual-aid.timeline',
			defaultMessage: 'Timeline',
			description: 'Remix option for displaying a timeline for the generated visual aids',
		},
		chatUpdateTimeline: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.timeline',
			defaultMessage: 'Update to a timeline',
			description: 'Chat update message when selecting the timeline remix subtype',
		},
		remixVariantGeographicalMap: {
			id: 'conversation-assistant.edit-with-rovo-bar.visual-aid.geographical-map',
			defaultMessage: 'Geographical map',
			description: 'Remix option for displaying a geographical map for the generated visual aids',
		},
		chatUpdateGeographicalMap: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.geographical-map',
			defaultMessage: 'Update to a geographical map',
			description: 'Chat update message when selecting the geographical map remix subtype',
		},
		remixVariantOrgChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.visual-aid.org-chart',
			defaultMessage: 'Org chart',
			description: 'Remix option for displaying an org chart for the generated visual aids',
		},
		chatUpdateOrgChart: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.org-chart',
			defaultMessage: 'Update to an org chart',
			description: 'Chat update message when selecting the org chart remix subtype',
		},
		remixVariantImage: {
			id: 'conversation-assistant.edit-with-rovo-bar.visual-aid.image',
			defaultMessage: 'Image',
			description: 'Remix option for displaying an image for the generated visual aids',
		},
		chatUpdateImage: {
			id: 'conversation-assistant.edit-with-rovo-bar.chat-update.image',
			defaultMessage: 'Update to an image',
			description: 'Chat update message when selecting the image remix subtype',
		},
		chartType: {
			id: 'conversation-assistant.edit-with-rovo-bar.type.charts',
			defaultMessage: 'Charts',
			description: 'Title for the chart category in the remix variants dropdown',
		},
		diagramType: {
			id: 'conversation-assistant.edit-with-rovo-bar.type.diagrams',
			defaultMessage: 'Diagrams',
			description: 'Title for the diagram category in the remix variants dropdown',
		},
		infographicType: {
			id: 'conversation-assistant.edit-with-rovo-bar.type.infographics',
			defaultMessage: 'Infographics',
			description: 'Title for the infographic category in the remix variants dropdown',
		},
		visualAidType: {
			id: 'conversation-assistant.edit-with-rovo-bar.type.visual-aids',
			defaultMessage: 'Visualizations',
			description: 'Title for the visual aid category in the remix variants dropdown',
		},
		changeChartTitle: {
			id: 'conversation-assistant.edit-with-rovo-bar.change-type-title.chart',
			defaultMessage: 'Change chart',
			description:
				'Tooltip shown on hover of the variant picker button when the current remix type is a chart',
		},
		changeDiagramTitle: {
			id: 'conversation-assistant.edit-with-rovo-bar.change-type-title.diagram',
			defaultMessage: 'Change diagram',
			description:
				'Tooltip shown on hover of the variant picker button when the current remix type is a diagram',
		},
		changeInfographicTitle: {
			id: 'conversation-assistant.edit-with-rovo-bar.change-type-title.infographic',
			defaultMessage: 'Change infographic',
			description:
				'Tooltip shown on hover of the variant picker button when the current remix type is an infographic',
		},
		changeVisualAidTitle: {
			id: 'conversation-assistant.edit-with-rovo-bar.change-type-title.visual-aid',
			defaultMessage: 'Change visualization',
			description:
				'Tooltip shown on hover of the variant picker button when the current remix type is a visualization',
		},
	});

export default message;
